import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  X,
  ShoppingBag,
  Boxes,
  Receipt,
  FileText,
  LayoutDashboard,
  MessageCircle,
} from 'lucide-react';

import AuthContext from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';
import { isSiteOnly } from '../utils/businessConfig';

const STATS_POLL_MS = 60000;

// Module-level cache for the quick-stats response. Base.js renders per page, so
// this FAB remounts on every navigation and previously refetched
// /dashboard/quick-stats/ each time. Reuse a fresh-enough value on remount
// instead of hitting the server again; the 60s poll still keeps it current.
const STATS_TTL_MS = 60000;
let statsCache = null; // { ts, data }

const ACTIONS = [
  { id: 'add_order', label: 'Add order', icon: ShoppingBag, route: '/orders-unified?quickadd=1' },
  { id: 'check_inventory', label: 'Check inventory', icon: Boxes, route: '/inventory' },
  { id: 'add_expense', label: 'Add expense', icon: Receipt, route: '/expenses?new=1' },
  { id: 'create_bill', label: 'Create bill', icon: FileText, route: '/payments?collect=1' },
  { id: 'view_dashboard', label: 'View dashboard', icon: LayoutDashboard, route: '/dashboard' },
];

// The WhatsApp support bubble that used to float as a SECOND button under this
// one (Base.js). Two floats stacked in the bottom-right corner covered card
// actions on every phone; it is now the last item of this menu.
const SUPPORT_ACTION = {
  id: 'support',
  label: 'Chat with Inikru support',
  icon: MessageCircle,
  route: 'https://api.whatsapp.com/send/?phone=9704387878&text&type=phone_number&app_absent=0',
};

// Screens that carry their own bottom bar on a phone — the till's running
// bill sits exactly where this button would float. No FAB there.
const HIDDEN_ON = ['/counter'];

// Module-level so a stats poll re-render never remounts the open menu
// (a component defined inside render is a NEW type every render).
const MenuRow = ({ action, onPick, className = '' }) => {
  const Icon = action.icon;
  return (
    <motion.li
      variants={{
        open: { opacity: 1, y: 0 },
        closed: { opacity: 0, y: 6 },
      }}
      transition={{ duration: 0.16, ease: 'easeOut' }}
      className={className}
      role="none"
    >
      <button
        type="button"
        role="menuitem"
        onClick={() => onPick(action)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-left text-[14px] font-display font-bold text-ink hover:bg-bone transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B40A0A]"
      >
        <span className="w-9 h-9 rounded-[10px] bg-bone border border-line grid place-items-center flex-shrink-0 text-ink">
          <Icon size={17} />
        </span>
        {action.label}
      </button>
    </motion.li>
  );
};

/**
 * QuickActionFAB
 * Site-wide floating action button that expands to a menu of quick actions.
 * Polls /api/dashboard/quick-stats/ every 60s for an action-item badge on the main button.
 */
const QuickActionFAB = () => {
  const { authToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState({ action_items: 0 });
  const containerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Poll quick stats
  useEffect(() => {
    if (!authToken?.access) return undefined;
    let cancelled = false;

    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/dashboard/quick-stats/`, {
          headers: { Authorization: `Bearer ${authToken.access}` },
        });
        const data = res.data || { action_items: 0 };
        statsCache = { ts: Date.now(), data };
        if (!cancelled && mountedRef.current) {
          setStats(data);
        }
      } catch (err) {
        devLog.error('Quick stats poll failed:', err);
      }
    };

    // Seed from a fresh-enough cache on remount and skip the immediate refetch;
    // otherwise fetch right away. The interval keeps it current either way.
    if (statsCache && Date.now() - statsCache.ts < STATS_TTL_MS) {
      setStats(statsCache.data);
    } else {
      fetchStats();
    }
    const id = setInterval(fetchStats, STATS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [authToken?.access]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  const handleAction = (action) => {
    setOpen(false);
    if (!action?.route) return;
    if (/^https?:\/\//i.test(action.route)) {
      window.open(action.route, '_blank', 'noopener,noreferrer');
    } else {
      navigate(action.route);
    }
  };

  // Don't render until authed — avoids leaking buttons on login/landing.
  // A site-only (portfolio) account has none of these actions: every one is
  // an order/stock/expense shortcut, so the FAB simply doesn't exist for it.
  if (!authToken?.access || isSiteOnly()) return null;
  if (HIDDEN_ON.some((p) => location.pathname.startsWith(p))) return null;

  const actionCount = Number(stats?.action_items || 0);
  const attention = actionCount > 0
    ? `${actionCount} thing${actionCount === 1 ? '' : 's'} need${actionCount === 1 ? 's' : ''} your attention today`
    : '';

  return (
    <div
      ref={containerRef}
      // One float in the corner: 12px above the 64px bottom nav on phones,
      // flush with the page corner on desktop.
      className="fixed bottom-[76px] right-4 lg:bottom-5 lg:right-5 z-40 flex flex-col items-end"
    >
      <AnimatePresence>
        {open && (
          <motion.ul
            key="fab-menu"
            initial="closed"
            animate="open"
            exit="closed"
            variants={{
              open: { transition: { staggerChildren: 0.03, delayChildren: 0.02 } },
              closed: { transition: { staggerChildren: 0.015, staggerDirection: -1 } },
            }}
            role="menu"
            aria-label="Quick actions"
            className="mb-3 w-[248px] max-w-[calc(100vw-2rem)] bg-white rounded-[12px] border-[1.5px] border-ink p-1.5 shadow-[4px_4px_0_#1B1B1B]"
          >
            {ACTIONS.map((action) => (
              <MenuRow key={action.id} action={action} onPick={handleAction} />
            ))}
            <MenuRow action={SUPPORT_ACTION} onPick={handleAction} className="mt-1.5 pt-1.5 border-t border-line" />
          </motion.ul>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close quick actions' : 'Open quick actions'}
        aria-expanded={open}
        aria-haspopup="menu"
        whileTap={{ scale: 0.92 }}
        title={attention ? `Quick actions — ${attention}` : 'Quick actions'}
        className="relative w-12 h-12 rounded-full flex items-center justify-center text-white bg-primary hover:bg-primary-dark transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B40A0A]"
      >
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center justify-center"
        >
          {open ? <X size={20} /> : <Plus size={20} />}
        </motion.span>
        {!open && actionCount > 0 && (
          <span
            title={attention}
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-ink text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
          >
            {actionCount > 9 ? '9+' : actionCount}
          </span>
        )}
      </motion.button>
    </div>
  );
};

export default QuickActionFAB;
